import { Button, Input, Loader, PasswordInput } from "@mantine/core";
import { IconAt, IconLock } from "@tabler/icons-react";
import { useCallback, useEffect, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IRotationModalProps } from "src/components/shared/Login/login.model";
import LoginWarningModal from "src/components/shared/Login/LoginWarningModal";
import RotationModal from "src/components/shared/Login/RotationModal";
import useLoginUser from "src/components/shared/Login/useLoginUser";
import { useGetAnalyticsData } from "src/hooks/useGetAnalyticsData";
import { type ILoginForm } from "src/interfaces";
import { Notification } from "src/utils/Notification";

interface Props {
  auditor?: boolean;
}

const LoginForm = ({ auditor }: Props) => {
  const navigate = useNavigate();
  const getAnalyticsData = useGetAnalyticsData();
  const [searchParams] = useSearchParams();
  const { register, handleSubmit } = useForm<ILoginForm>();
  const [rotationModalProps, setRotationModalProps] =
    useState<IRotationModalProps>();
  const [updateBlocked, setUpdateBlocked] = useState(false);

  useEffect(() => {
    try {
      if (window?.api?.checkAppAccess) {
        window.api.checkAppAccess((accessInfo: any) => {
          setUpdateBlocked(
            !accessInfo.canUseApp ||
              (accessInfo.updateAvailable && !accessInfo.updateDownloaded),
          );
        });
      }
      if (window?.api?.onUpdateRequired) {
        window.api.onUpdateRequired(() => {
          setUpdateBlocked(true);
        });
      }
    } catch (error) {
      console.error("Error setting up update listeners:", error);
      // Ensure login is not blocked if there's an error
      setUpdateBlocked(false);
    }
  }, []);

  const { handleLogin, isLoading, loginWarning, dismissLoginWarning } =
    useLoginUser({
      setRotationModalProps,
    });

  const handleRegister = useCallback(() => {
    const code = searchParams.get("code");
    navigate("/auditor/register?code=" + code);
  }, [navigate, searchParams]);

  const onSubmit: SubmitHandler<ILoginForm> = useCallback(
    async data => {
      const params: ILoginForm & { code?: string | null } = data;
      if (auditor) {
        const code = searchParams.get("code");
        if (!code) {
          Notification(
            "Error",
            "Your code is invalid, make sure you use the link from our mail.",
            "red",
          );
          return;
        }
        params.code = code;
      }
      const { appVersion, analytics } = getAnalyticsData();
      params.appVersion = appVersion;
      params.analytics = analytics;
      await handleLogin(params);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getAnalyticsData is recreated each render; listing it would rebuild the submit handler every render
    [auditor, handleLogin, searchParams],
  );

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Input
          placeholder="Email Address"
          leftSection={<IconAt size="1rem" />}
          autoComplete="username"
          {...register("email", { required: true })}
        />
        <PasswordInput
          mt={10}
          placeholder="Password"
          leftSection={<IconLock size="1rem" />}
          withAsterisk
          autoComplete="current-password"
          {...register("pass", { required: true })}
        />
        <Button w="100%" mt={10} type="submit" disabled={updateBlocked}>
          {isLoading ? <Loader color="#fff" size={16} /> : "Log in"}
        </Button>
        {auditor && (
          <Button
            w="100%"
            mt={10}
            variant="white"
            onClick={handleRegister}
            disabled={updateBlocked}>
            Register instead
          </Button>
        )}
      </form>

      <RotationModal
        {...rotationModalProps}
        onYes={d => onSubmit(d)}
        close={() => setRotationModalProps({ open: false })}
      />
      <LoginWarningModal
        warningData={loginWarning}
        onDismiss={dismissLoginWarning}
      />
    </>
  );
};

export default LoginForm;
